import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getArticle, getArticles, markArticleRead } from 'students/api/articles';
import { IArticle, IArticleDetail } from 'students/api/articles/types';

interface IArticlesState {
  items: IArticle[];
  nextPage: number | null;
  unreadCount: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  isPanelOpen: boolean;
  currentArticle: IArticleDetail | null;
  isArticleLoading: boolean;
  error: string | null;
}

const initialState: IArticlesState = {
  items: [],
  nextPage: null,
  unreadCount: 0,
  isLoading: false,
  isLoadingMore: false,
  isPanelOpen: false,
  currentArticle: null,
  isArticleLoading: false,
  error: null
};

export const fetchArticles = createAsyncThunk('articles/fetchArticles', async () => {
  return getArticles(1);
});

export const fetchMoreArticles = createAsyncThunk(
  'articles/fetchMoreArticles',
  async (page: number) => {
    return getArticles(page);
  }
);

export const fetchArticle = createAsyncThunk(
  'articles/fetchArticle',
  async (id: number) => {
    return getArticle(id);
  }
);

export const markAsRead = createAsyncThunk('articles/markAsRead', async (id: number) => {
  await markArticleRead(id);
  return id;
});

const articlesSlice = createSlice({
  name: 'articles',
  initialState,
  reducers: {
    openPanel(state) {
      state.isPanelOpen = true;
    },
    closePanel(state) {
      state.isPanelOpen = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArticles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.articles;
        state.nextPage = action.payload.nextPage;
        state.unreadCount = action.payload.unreadCount;
      })
      .addCase(fetchArticles.rejected, (state) => {
        state.isLoading = false;
        state.error = 'Failed to load articles';
      })
      .addCase(fetchMoreArticles.pending, (state) => {
        state.isLoadingMore = true;
      })
      .addCase(fetchMoreArticles.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        state.items = [...state.items, ...action.payload.articles];
        state.nextPage = action.payload.nextPage;
      })
      .addCase(fetchMoreArticles.rejected, (state) => {
        state.isLoadingMore = false;
      })
      .addCase(fetchArticle.pending, (state) => {
        state.isArticleLoading = true;
        state.currentArticle = null;
        state.error = null;
      })
      .addCase(fetchArticle.fulfilled, (state, action) => {
        state.isArticleLoading = false;
        state.currentArticle = action.payload;
      })
      .addCase(fetchArticle.rejected, (state) => {
        state.isArticleLoading = false;
        state.error = 'Failed to load article';
      })
      .addCase(markAsRead.fulfilled, (state, action: PayloadAction<number>) => {
        const item = state.items.find((a) => a.id === action.payload);
        if (item?.unread) {
          item.unread = false;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        if (state.currentArticle?.id === action.payload) {
          state.currentArticle.unread = false;
        }
      });
  }
});

export const articlesActions = articlesSlice.actions;
export default articlesSlice.reducer;
